import {
  Alignment,
  Button,
  Classes,
  Collapse,
  Colors,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classnames from "classnames";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";

import { useAuth, useNavigationContext } from "../../contexts";
import { useTl } from "../../hooks";
import { CollapseAllContext } from "./CollapseAllContext";
import { Container } from "./Container";
import { INavigationItem } from "./INavigationItem";

const ButtonStyled = styled(Button)`
  /* Change colors for customer */
  margin: 0em;

  &.level_0 {
    background-color: ${Colors.BLUE1} !important;
    border-radius: 0;
    padding: 0.5em !important;
    padding-left: 15px !important;

    &:hover {
      background-color: ${Colors.BLUE3} !important;
    }
  }

  &.level_1.has-children {
    /* border: 1px solid white; */
    margin-top: 1px;
    margin-bottom: 1px;
    background-color: ${Colors.BLUE3} !important;
    border-radius: 0;

    &:hover {
      background-color: ${Colors.BLUE3} !important;
    }
  }

  &.level_1 {
    padding-left: 25px !important;
  }

  &.is-active {
    font-weight: bold;
    background-color: ${Colors.GRAY1} !important;
    text-decoration: blink !important;
  }
`;

export interface IMenuItemProps {
  item: INavigationItem;
  level: number;
  disabled?: boolean;
  maxAutoCollapseLevel?: number;
  onNavigation?: () => void;
}

export const MenuItem: React.FunctionComponent<IMenuItemProps> = ({
  item,
  level,
  disabled,
  maxAutoCollapseLevel = 999,
  onNavigation,
}) => {
  const location = useLocation();
  const isActive = React.useMemo(
    () => item.route && location.pathname.startsWith(item.route),
    [item, location]
  );

  const [isExpanded, setIsExpanded] = React.useState(!!item.defaultOpened);
  const history = useHistory();
  const { t } = useTl();
  const id = React.useMemo(() => uuidv4(), []);
  const { hasAction } = useAuth();
  const { openedMenuId, setMenuOpenedId } =
    React.useContext(CollapseAllContext);

  const { clearBackRoute } = useNavigationContext();

  React.useEffect(() => {
    const hasItemWithCurrentRoute = (itm: INavigationItem) => {
      if (itm.route && location.pathname.includes(itm.route)) {
        return true;
      }
      if (itm.items) {
        return itm.items.filter((i) => hasItemWithCurrentRoute(i)).length > 0;
      }
      return false;
    };

    if (hasItemWithCurrentRoute(item)) {
      setIsExpanded(true);
      setMenuOpenedId({ level, id });
    }
  }, [id, item, level, location.pathname, setMenuOpenedId]);

  React.useEffect(() => {
    if (
      openedMenuId?.level === level &&
      openedMenuId?.id !== id &&
      level <= maxAutoCollapseLevel
    ) {
      setIsExpanded(false);
    }
  }, [id, level, maxAutoCollapseLevel, openedMenuId]);

  const hasChildren = React.useMemo(() => {
    return item.items && item.items.length > 0;
  }, [item]);

  const collapseIcon = React.useMemo(() => {
    if (hasChildren) {
      return isExpanded ? IconNames.CHEVRON_RIGHT : IconNames.CHEVRON_DOWN;
    } else {
      return IconNames.BLANK;
    }
  }, [hasChildren, isExpanded]);

  const onClick = React.useCallback(() => {
    if (hasChildren) {
      if (!isExpanded) {
        setMenuOpenedId({ level, id });
      }
      setIsExpanded((e) => !e);
    } else if (item.route) {
      clearBackRoute();
      history.push(item.route);
      if (onNavigation) {
        onNavigation();
      }
    }
  }, [
    hasChildren,
    item.route,
    isExpanded,
    setMenuOpenedId,
    level,
    id,
    clearBackRoute,
    history,
    onNavigation,
  ]);

  const finalSubItems = React.useMemo(() => {
    if (!item.items) return [];
    return item.items.filter((d) => hasAction(d.actions));
  }, [hasAction, item.items]);

  return (
    <>
      {item.startGroupTitle && (
        <div style={{ color: "red" }}>{item.startGroupTitle}</div>
      )}
      <ButtonStyled
        text={t(item.name)}
        icon={
          item.alternativeIcon ? (
            <i className={`${Classes.ICON} ${item.alternativeIcon}`}></i>
          ) : (
            item.icon
          )
        }
        rightIcon={collapseIcon}
        onClick={onClick}
        style={{ paddingLeft: `${level * 1 + 0.1}em` }}
        minimal={true}
        className={classnames([
          `level_${level}`,
          { "has-children": hasChildren, "is-active": isActive },
        ])}
        alignText={Alignment.LEFT}
        small={false}
        disabled={disabled}
      />
      <Collapse isOpen={isExpanded}>
        <Container>
          {finalSubItems.map((itm, index) => (
            <MenuItem
              key={index}
              item={itm}
              level={level + 1}
              disabled={itm.disabled}
              maxAutoCollapseLevel={maxAutoCollapseLevel}
              onNavigation={onNavigation}
            />
          ))}
        </Container>
      </Collapse>
    </>
  );
};
